<script setup>
  import { ref, computed, onMounted, useTemplateRef } from 'vue';
  import { useElementSize } from '@vueuse/core';
  import ModalSiteSettings from '@/components/ModalSiteSettings.vue';
  import WidgetsPageHeader from '@/components/WidgetsPageHeader.vue';
  import WidgetEmbedBlock from '@/components/WidgetEmbedBlock.vue';
  import WidgetEditor from '@/components/WidgetEditor.vue';
  import ModalWidgetLogos from '@/components/ModalWidgetLogos.vue';
  import WidgetLogosButton from '@/components/WidgetLogosButton.vue';
  import ErrorBlock from '@/components/ErrorBlock.vue';
  import UpgradeBlock from '@/components/UpgradeBlock.vue';
  import Icon from '@/components/icons/Icon.vue';
  import SegmentedControl from '@/components/controls/SegmentedControl.vue';
  import SegmentedControlButton from '@/components/controls/SegmentedControlButton.vue';
  import { useSpotStore } from '@/stores/SpotStore';
  import { useUserStore } from '@/stores/UserStore';
  import { useWidgetStore } from '@/stores/WidgetStore';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import { useUserUpgrades } from '@/composables/useUserUpgrades';
  import { useI18n } from 'vue-i18n';

  const spotStore = useSpotStore();
  const userStore = useUserStore();
  const widgetStore = useWidgetStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  // The Global Settings toolbar, by its width: narrower than this, Download Logos moves under the
  // widget cards and the settings fill the toolbar
  const TOOLBAR_LOGOS_MIN_WIDTH = 1080;
  // Narrower than this, the settings go under a "Global Settings" disclosure
  const TOOLBAR_DISCLOSURE_MAX_WIDTH = 960;
  // Members below this level (Plus) don't get wind speed and gusts on the widgets
  const WIDGET_WIND_MEMBER_LEVEL = 46;
  // Statuses that leave no widgets to show, and the i18n keys for their message
  const LOAD_ERROR_KEYS = {
    'not-found': 'widgets.notFound',
    error: 'widgets.loadError'
  };

  const { t } = useI18n();
  // The upgrade banner's levels and button (anonymous visitors are offered the free trial)
  const { upgrade } = useUserUpgrades();

  const toolbarElement = useTemplateRef('toolbar-element');

  const { width: toolbarWidth } = useElementSize(toolbarElement);

  const showLogosModal = ref(false);
  const showGlobalSettings = ref(true);

  // Measured widths only (0 = not measured yet): until then, the wide layout
  const logosInToolbar = computed(() => !toolbarWidth.value || toolbarWidth.value >= TOOLBAR_LOGOS_MIN_WIDTH);
  const useSettingsDisclosure = computed(() => !!toolbarWidth.value && toolbarWidth.value < TOOLBAR_DISCLOSURE_MAX_WIDTH);
  const showSettings = computed(() => !useSettingsDisclosure.value || showGlobalSettings.value);

  // Why there are no widgets to show: the spot isn't found, or it couldn't be loaded
  const loadError = computed(() => {
    const key = LOAD_ERROR_KEYS[widgetStore.status];

    if (!key) return null;

    return { header: t(`${key}.header`), title: t(`${key}.title`), message: t(`${key}.message`) };
  });

  const showUpgradeBlock = computed(() => userStore.initialized && userStore.memberLevel < WIDGET_WIND_MEMBER_LEVEL && !loadError.value);

  // The color field is typed into freely; until it holds a hex color there's no color to show
  const hasDefaultColor = computed(() => /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(widgetStore.defaults.color?.trim() ?? ''));

  const speedOptions = computed(() => ({
    mph: t('siteSettings.milesPerHour'),
    kph: t('siteSettings.kilometersPerHour'),
    kts: t('siteSettings.knots'),
    mps: t('siteSettings.metersPerSecond')
  }));  

  const tempOptions = computed(() => ({
    f: t('siteSettings.fahrenheit'),
    c: t('siteSettings.celsius'),
  }));  

  const unitLabels = {
    mph: 'mph',
    kph: 'kph',
    kts: 'kts',
    mps: 'm/s',
    f: '°F',
    c: '°C'
  };

  function sendGlobalUnitsEvent(unit) {
    googleAnalyticsStore.sendEvent('Web_Widget_Global_Units_Changed', {
      Spot_Name: widgetStore.analyticsSpotName,
      Unit: unit
    });
  }

  // segmentChanged only comes from the user picking a unit, never from a write-back
  function handleUnitsWindChange({ value }) {
    sendGlobalUnitsEvent(`Speed-${value}`);
  }

  function handleUnitsTempChange({ value }) {
    sendGlobalUnitsEvent(`Temperature-${value}`);
  }

  function handleOpenLogos() {
    showLogosModal.value = true;

    googleAnalyticsStore.sendEvent('Web_Widget_Logo_Download_Open', {
      Spot_Name: widgetStore.analyticsSpotName
    });
  }

  // On change, once the title is committed, not on every keystroke
  function handleGlobalTitleChange() {
    googleAnalyticsStore.sendEvent('Web_Widget_Global_Title_Changed', {
      Spot_Name: widgetStore.analyticsSpotName
    });
  }

  function handleGlobalSettingsToggle() {
    showGlobalSettings.value = !showGlobalSettings.value;
  }

  // A color chosen in the Global Settings joins the editor's custom swatches too (on change: once
  // the picker closes or the typed color is committed, not on every keystroke)
  function handleDefaultColorChange() {
    if (!widgetStore.isHexColor(widgetStore.defaults.color)) return;

    widgetStore.addCustomColor(widgetStore.defaults.color);

    googleAnalyticsStore.sendEvent('Web_Widget_Global_Color_Changed', {
      Spot_Name: widgetStore.analyticsSpotName
    });
  }

  function handleReset() {
    widgetStore.resetAll();
  }

  onMounted(async () => {
    await userStore.ready;
    widgetStore.initializeDefaults();

    await userStore.getProfiles();

    await widgetStore.loadSpot();
  });
</script>

<template>
  <!-- Wind Widgets page content — ported from WindAlert-Web en-us/Widgets/WindWidgets.aspx (LIF-122) -->
  <div class="jwx-page__container">
      <div class="jwx-page" data-container-gap="true">
        <WidgetsPageHeader v-if="userStore.initialized && spotStore.spot" />
        <UpgradeBlock
          v-if="showUpgradeBlock"
          :upgrade="upgrade"
          :productText="t('widgets.upgradeProduct')"
          clickLocation="Wind Widgets Banner"
        />
        <div class="jwx-page__main">
          <ErrorBlock v-if="loadError" :error="loadError" />

          <div
            v-if="userStore.initialized && !loadError"
            ref="toolbar-element"
            class="jwx-control-bar"
            data-padded="true"
            :data-stacked="useSettingsDisclosure || null"
            :data-fill="!logosInToolbar || null"
          >
            <div class="jwx-control-bar__controls-left">
              <!-- Stacked, Reset is an icon in the header, just left of the disclosure's chevron -->
              <div v-if="useSettingsDisclosure" class="jwx-control-bar__disclosure-header">
                <button
                  @click="handleGlobalSettingsToggle"
                  type="button"
                  class="jwx-control-bar__disclosure"
                  :aria-expanded="showGlobalSettings"
                  aria-controls="widget-global-settings"
                >
                  {{ t('widgets.globalSettings') }}
                  <Icon name="chevron-down" class="jwx-control-bar__disclosure-icon" />
                </button>
                <button
                  @click="handleReset"
                  type="button"
                  class="jwx-button-control jwx-control-bar__reset"
                  data-surface="0"
                  data-shape="pill"
                  data-icon-only
                  :title="t('widgets.resetSettings')"
                  :disabled="!widgetStore.isCustomized"
                >
                  <Icon name="rotate-right" class="jwx-button-control__icon" />
                  <span class="sr-only">{{ t('widgets.resetSettings') }}</span>
                </button>
              </div>
              <p v-else class="jwx-control-bar__label">{{ t('widgets.globalSettings') }}</p>
              <div v-show="showSettings" id="widget-global-settings" class="jwx-control-bar__disclosure-content">
                <label class="jwx-control-bar__text-input-control" data-grow>
                  <span class="jwx-control-bar__text-input-label">{{ t('widgets.settings.title') }}</span>
                  <input 
                    type="text" 
                    v-model="widgetStore.defaults.spotName"
                    @change="handleGlobalTitleChange"
                    name="spot-name-color-input" 
                    id="spot-name-color-input" 
                    class="jwx-control-bar__text-input"
                  >
                </label>
                <!-- Units and color wrap below the title together; where they don't fit on one
                     row, they stack together -->
                <div class="jwx-control-bar__wrap-group">
                  <div class="jwx-control-bar__group" data-wrap-together>
                    <!-- Speed and temperature stay on one row, even stacked -->
                    <div class="jwx-control-bar__group" data-units>
                      <SegmentedControl
                        v-model="widgetStore.defaults.unitsWind"
                        @segmentChanged="handleUnitsWindChange"
                        :label="t('siteSettings.windSpeed')"
                        style="--segmented-control-background-color: var(--jwx-control-background-color-level-1);"
                      >
                        <SegmentedControlButton
                          v-for="(option, key) of speedOptions"
                          :key="key"
                          :value="key"
                        >
                          {{ unitLabels[key] }}
                        </SegmentedControlButton>
                      </SegmentedControl>
    
                      <SegmentedControl
                        v-model="widgetStore.defaults.unitsTemp"
                        @segmentChanged="handleUnitsTempChange"
                        :label="t('siteSettings.temperature')"
                        style="--segmented-control-background-color: var(--jwx-control-background-color-level-1);"
                      >
                        <SegmentedControlButton
                          v-for="(option, key) of tempOptions"
                          :key="key"
                          :value="key"
                        >
                          {{ unitLabels[key] }}
                        </SegmentedControlButton>
                      </SegmentedControl>
                    </div>

                    <div class="jwx-control-bar__group">
                      <div class="jwx-control-bar__color-picker-control">
                        <input 
                          type="color"
                          v-model="widgetStore.defaults.color"
                          @change="handleDefaultColorChange"
                          name="default-widget-color-picker-input" 
                          id="default-widget-color-picker-input"
                          class="jwx-control-bar__color-picker-input"
                        >
                        <label
                          for="default-widget-color-picker-input"
                          class="jwx-control-bar__color-picker-button"
                          :data-empty="!hasDefaultColor || null"
                          :style="`--jwx-control-bar-color-picker-color: ${widgetStore.defaults.color}`"
                        ></label>
                      </div>
                      <label class="jwx-control-bar__text-input-control" data-grow>
                        <span class="jwx-control-bar__text-input-label">{{ t('widgets.settings.color') }}</span>
                        <input 
                          type="text" 
                          v-model="widgetStore.defaults.color"
                          @change="handleDefaultColorChange"
                          name="default-widget-color-input" 
                          id="default-widget-color-input" 
                          class="jwx-control-bar__text-input"
                        >
                      </label>
                    </div>
                  </div>
                </div>
                <button
                  v-if="!useSettingsDisclosure"
                  @click="handleReset"
                  type="button"
                  class="jwx-button-control jwx-control-bar__reset"
                  data-surface="0"
                  data-shape="pill"
                  :title="t('widgets.resetSettings')"
                  :disabled="!widgetStore.isCustomized"
                >
                  <Icon name="rotate-right" class="jwx-button-control__icon" />
                  {{ t('common.reset') }}
                </button>
              </div>
            </div>
            <div v-if="widgetStore.logoDownloads && logosInToolbar" class="jwx-control-bar__controls-right">
              <WidgetLogosButton @open="handleOpenLogos" />
            </div>
          </div>
          <div v-if="widgetStore.hasSpot" class="jwx-widget-gallery jwx-m-top-xl">
            <WidgetEmbedBlock
              v-for="widgetId in widgetStore.availableWidgets"
              :key="widgetId"
              :widgetId="widgetId"
            />
          </div>
    
          <div v-if="widgetStore.logoDownloads && !logosInToolbar && !loadError" class="jwx-widget-page__logos">
            <WidgetLogosButton placement="page" @open="handleOpenLogos" />
          </div>
        </div>
    </div>
  </div>

  <WidgetEditor />
  <ModalWidgetLogos v-if="widgetStore.logoDownloads" v-model="showLogosModal" />

  <!-- This page owns the JWX markers on WindWidgets.aspx, so it renders the settings modal
       in place of the standalone settings-modal-global-lifestyle bundle. -->
  <Suspense>
    <ModalSiteSettings :show="userStore.showSettingsModal" />
  </Suspense>
</template>
